import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Star, Ban, Save, Plus, Send, Trash2, FileText, MessageSquare, Award, Users2, AlertCircle, ScrollText, Clock, Pencil, Sparkles, Wallet, Utensils, Wrench, BarChart3, Check } from "lucide-react";
import { guestAPI, VIP_STYLE, cls, fmtDT, fmtD, fmtINR, initials } from "./guestApi";
import { DatePicker } from "@/components/ui/date-picker";
import { GuestStayDocuments } from "@/components/documents/GuestStayDocuments";
import { NumberInput } from "@/components/ui/number-input";

const TABS = [
  { k: "overview",    label: "Overview",       icon: Sparkles },
  { k: "stays",       label: "Reservations",   icon: Star },
  { k: "wallet",      label: "Wallet",         icon: Wallet },
  { k: "dining",      label: "Dining",         icon: Utensils },
  { k: "housekeeping", label: "Housekeeping",  icon: Sparkles },
  { k: "maintenance", label: "Maintenance",    icon: Wrench },
  { k: "preferences", label: "Preferences",    icon: Award },
  { k: "timeline",    label: "Timeline",       icon: Clock },
  { k: "documents",   label: "Documents",      icon: FileText },
  { k: "communication", label: "Communications", icon: MessageSquare },
  { k: "loyalty",     label: "Loyalty",        icon: Award },
  { k: "family",      label: "Family",         icon: Users2 },
  { k: "complaints",  label: "Complaints",     icon: AlertCircle },
  { k: "analytics",   label: "Analytics",      icon: BarChart3 },
  { k: "audit",       label: "Audit",          icon: ScrollText },
];

export default function GuestProfileDrawer({ guestId, onClose, onChanged }) {
  const [g, setG]     = useState(null);
  const [tab, setTab] = useState("overview");

  const load = () => guestAPI.get(guestId).then(setG).catch(()=>{});
  useEffect(() => { load(); }, [guestId]);   // eslint-disable-line

  if (!g) return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 text-[13px] text-slate-500">Loading…</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-slate-50 w-full max-w-4xl shadow-2xl h-full flex flex-col"
           onClick={e=>e.stopPropagation()} data-testid={`guest-drawer-${g.id}`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-hocp-navy text-white text-[18px] font-semibold flex items-center justify-center shrink-0">
            {g.photo_url
              ? <img src={g.photo_url} alt="" className="w-full h-full rounded-2xl object-cover"/>
              : initials(g)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-[20px] font-bold text-hocp-navy">
                {g.first_name} {g.last_name || ""}
              </h2>
              <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-medium capitalize ${VIP_STYLE[g.vip_level] || VIP_STYLE.standard}`}>{g.vip_level}</span>
              {g.blacklist?.active && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-medium bg-rose-100 text-rose-700"><Ban className="w-3 h-3"/> Blacklisted</span>}
              {g.stats?.is_current && <span className="px-2 py-0.5 rounded-md text-[10.5px] font-medium bg-emerald-100 text-emerald-700">In-House · Room {g.current_stay?.room || "?"}</span>}
            </div>
            <div className="text-[12px] text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
              <span className="mono">{g.email || "no email"}</span>
              <span className="mono">{g.phone || ""}</span>
              <span>{g.nationality || ""}</span>
              {g.company_name && <span>· {g.company_name}</span>}
            </div>
          </div>
          <button onClick={onClose} data-testid="guest-drawer-close"
                  className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 bg-white px-6 flex gap-1 overflow-x-auto shrink-0">
          {TABS.map(t => {
            const I = t.icon;
            return (
              <button key={t.k} onClick={()=>setTab(t.k)}
                      data-testid={`guest-drawer-tab-${t.k}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-[12.5px] font-medium border-b-2 whitespace-nowrap ${
                        tab===t.k ? "border-hocp-navy text-hocp-navy" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                <I className="w-3.5 h-3.5"/>{t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === "overview"      && <TabOverview g={g} onChanged={()=>{load(); onChanged?.();}}/>}
          {tab === "stays"         && <TabStays g={g} onChanged={()=>{load(); onChanged?.();}}/>}
          {tab === "wallet"        && <TabWallet g={g}/>}
          {tab === "dining"        && <TabDining gid={g.id}/>}
          {tab === "housekeeping"  && <TabHousekeeping gid={g.id}/>}
          {tab === "maintenance"   && <TabMaintenance gid={g.id}/>}
          {tab === "preferences"   && <TabPreferences g={g} onChanged={load}/>}
          {tab === "timeline"      && <TabTimeline gid={g.id}/>}
          {tab === "documents"     && <TabDocuments gid={g.id}/>}
          {tab === "communication" && <TabCommunication gid={g.id}/>}
          {tab === "loyalty"       && <TabLoyalty gid={g.id}/>}
          {tab === "family"        && <TabFamily gid={g.id}/>}
          {tab === "complaints"    && <TabComplaints gid={g.id}/>}
          {tab === "analytics"     && <TabAnalytics gid={g.id}/>}
          {tab === "audit"         && <TabAudit gid={g.id}/>}
        </div>
      </div>
    </div>
  );
}

// ─── Overview ───
function TabOverview({ g, onChanged }) {
  const [saving, setSaving] = useState(false);
  const s = g.stats || {};
  const toggleBlacklist = async () => {
    if (g.blacklist?.active) {
      if (!window.confirm("Unblock this guest?")) return;
      try { await guestAPI.unblacklist(g.id); toast.success("Unblocked"); onChanged(); }
      catch { toast.error("Failed"); }
    } else {
      const reason = window.prompt("Reason for blacklisting?");
      if (!reason) return;
      try { await guestAPI.blacklist(g.id, { reason }); toast.success("Blacklisted"); onChanged(); }
      catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }
  };
  return (
    <div className="space-y-4" data-testid="guest-tab-overview">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Lifetime Stays" value={s.stays || 0}/>
        <StatCard label="Nights" value={s.nights || 0}/>
        <StatCard label="Lifetime Revenue" value={fmtINR(s.revenue || 0)}/>
        <StatCard label="Complaints" value={s.complaints || 0} sub={s.complaints_open ? `${s.complaints_open} open` : ""}/>
      </div>

      {g.current_stay && (
        <div className="bg-emerald-50/50 border border-emerald-200 rounded-xl p-4" data-testid="guest-current-stay">
          <div className="text-[10.5px] uppercase tracking-wider text-emerald-700 font-semibold">Current Stay</div>
          <div className="mt-1 flex items-center gap-4 text-[13px]">
            <span className="font-semibold text-hocp-navy">Room {g.current_stay.room}</span>
            <span className="text-slate-600">{g.current_stay.room_type}</span>
            <span className="text-slate-500 mono">{g.current_stay.arrival} → {g.current_stay.departure}</span>
            <span className="text-slate-500">{g.current_stay.adults}A/{g.current_stay.children}C</span>
            {g.current_stay.revenue && <span className="ml-auto font-medium text-emerald-700">{fmtINR(g.current_stay.revenue)}</span>}
          </div>
        </div>
      )}

      <EditProfileForm g={g} onDone={onChanged}/>

      <div className="flex gap-2">
        <button onClick={toggleBlacklist} data-testid="guest-toggle-blacklist"
                className={`h-9 px-4 rounded-lg text-[12.5px] font-medium inline-flex items-center gap-1.5 ${
                  g.blacklist?.active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200" :
                  "bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200"}`}>
          <Ban className="w-3.5 h-3.5"/>
          {g.blacklist?.active ? "Unblock Guest" : "Blacklist Guest"}
        </button>
      </div>

      {g.blacklist?.active && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-[12px] text-rose-800">
          <div className="font-medium">Blacklist warning</div>
          <div>{g.blacklist.reason}</div>
          <div className="text-[10.5px] mono opacity-60 mt-1">by {g.blacklist.actor_email} · {fmtDT(g.blacklist.at)}</div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">{label}</div>
      <div className="mt-1 font-display text-[20px] font-semibold text-hocp-navy tabular-nums">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500">{sub}</div>}
    </div>
  );
}

function EditProfileForm({ g, onDone }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (edit) setF({ first_name:g.first_name, last_name:g.last_name, email:g.email, phone:g.phone,
                      nationality:g.nationality, language:g.language, gender:g.gender, dob:g.dob,
                      vip_level:g.vip_level, vip_notes:g.vip_notes, company_name:g.company_name,
                      gst_number:g.gst_number });
  }, [edit, g]);

  const save = async () => {
    setSaving(true);
    try { await guestAPI.update(g.id, { ...f, reason: "manual edit" }); toast.success("Updated"); setEdit(false); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!edit) return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="guest-details">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">Personal Details</div>
        <button onClick={()=>setEdit(true)} data-testid="guest-details-edit"
                className="ml-auto h-7 px-2 rounded-lg text-[11.5px] text-slate-500 hover:bg-slate-100 inline-flex items-center gap-1">
          <Pencil className="w-3 h-3"/> Edit
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 text-[12.5px]">
        <Field label="Gender" value={g.gender}/>
        <Field label="DOB" value={g.dob ? fmtD(g.dob) : "—"}/>
        <Field label="Language" value={g.language}/>
        <Field label="Nationality" value={g.nationality}/>
        <Field label="Birthday" value={g.dates?.birthday ? fmtD(g.dates.birthday) : "—"}/>
        <Field label="Anniversary" value={g.dates?.anniversary ? fmtD(g.dates.anniversary) : "—"}/>
        <Field label="Occupation" value={g.occupation}/>
        <Field label="Company" value={g.company_name}/>
        <Field label="GST" value={g.gst_number}/>
        <Field label="Passport" value={g.passport?.number}/>
        <Field label="Gov ID" value={g.government_id?.number}/>
        <Field label="Emergency" value={g.emergency_contact?.name}/>
      </div>
      {g.vip_notes && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
          <div className="text-[10.5px] uppercase text-amber-700 font-semibold mb-0.5">VIP Notes</div>
          <div className="text-[12.5px] text-amber-900">{g.vip_notes}</div>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-3">Editing Personal Details</div>
      <div className="grid grid-cols-2 gap-3">
        {["first_name","last_name","email","phone","nationality","language","occupation","company_name","gst_number","vip_notes"].map(k => {
          const lbl = k.replace(/_/g," ").replace(/\b\w/g, c => c.toUpperCase());
          return (
            <label key={k} className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">{lbl}</span>
              <input value={f[k] || ""} onChange={e=>setF({...f, [k]: e.target.value})} placeholder={lbl}
                     data-testid={`guest-edit-${k}`} className={cls + " w-full"}/></label>
          );
        })}
        <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">VIP level</span>
          <select value={f.vip_level || ""} onChange={e=>setF({...f, vip_level: e.target.value})} data-testid="guest-edit-vip" className={cls + " w-full"}>
            <option value="standard">Standard</option><option value="silver">Silver</option>
            <option value="gold">Gold</option><option value="platinum">Platinum</option>
          </select></label>
        <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Gender</span>
          <select value={f.gender || ""} onChange={e=>setF({...f, gender: e.target.value})} className={cls + " w-full"}>
            <option value="">Select…</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
          </select></label>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={()=>setEdit(false)} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="guest-edit-save"
                className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
          <Save className="w-4 h-4"/> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] text-slate-400 uppercase tracking-wider">{label}</div>
      <div className="text-slate-700 font-medium">{value || "—"}</div>
    </div>
  );
}

// ─── Stays ───
function TabStays({ g, onChanged }) {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = () => guestAPI.stays(g.id).then(setRows);
  useEffect(() => { load(); }, []);   // eslint-disable-line
  return (
    <div className="space-y-3" data-testid="guest-tab-stays">
      <div className="flex justify-end">
        <button onClick={()=>setCreating(true)} data-testid="guest-stay-new"
                className="h-9 px-3 rounded-lg bg-hocp-gold text-hocp-eerie text-[12.5px] font-medium inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5"/> Add Stay
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {rows.length === 0
          ? <div className="p-8 text-center text-[13px] text-slate-400">No stays yet.</div>
          : <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr><th className="text-left px-4 py-2">Arrival</th><th className="text-left px-4 py-2">Departure</th>
                    <th className="text-left px-4 py-2">Room</th><th className="text-center px-4 py-2">Guests</th>
                    <th className="text-right px-4 py-2">Revenue</th><th className="text-center px-4 py-2">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(s => (
                  <tr key={s.id} data-testid={`guest-stay-${s.id}`}>
                    <td className="px-4 py-2 mono">{s.arrival}</td>
                    <td className="px-4 py-2 mono">{s.departure}</td>
                    <td className="px-4 py-2">{s.room} <span className="text-[11px] text-slate-500">{s.room_type}</span></td>
                    <td className="px-4 py-2 text-center">{s.adults}A/{s.children}C</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtINR(s.revenue || 0)}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${
                        s.status === "checked_in" ? "bg-emerald-100 text-emerald-700" :
                        s.status === "checked_out" ? "bg-slate-100 text-slate-600" :
                        s.status === "cancelled" ? "bg-rose-100 text-rose-700" : "bg-sky-100 text-sky-700"}`}>{(s.status||"").replace("_"," ")}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
      {creating && <NewStayDialog gid={g.id} onClose={()=>setCreating(false)} onDone={()=>{ setCreating(false); load(); onChanged?.(); }}/>}
    </div>
  );
}

function NewStayDialog({ gid, onClose, onDone }) {
  const [f, setF] = useState({ guest_id: gid, room:"", room_type:"", arrival:"", departure:"",
                                  adults:1, children:0, revenue:0, status:"reserved", rate_plan:"" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.arrival || !f.departure) { toast.error("Arrival + departure required"); return; }
    setSaving(true);
    try { await guestAPI.createStay({ ...f, revenue: Number(f.revenue) || 0,
                                       adults: Number(f.adults) || 1, children: Number(f.children) || 0 });
         toast.success("Stay added"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">New Stay</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Room</span>
            <input placeholder="e.g. 301" value={f.room} onChange={e=>setF({...f, room: e.target.value})} data-testid="guest-stay-room" className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Room type</span>
            <input placeholder="e.g. Deluxe" value={f.room_type} onChange={e=>setF({...f, room_type: e.target.value})} className={cls + " w-full"}/></label>
          <div><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Arrival</span>
            <DatePicker value={(f.arrival || '').slice(0,10)} onChange={(_v) => setF({...f, arrival: _v})} data-testid="guest-stay-arrival"/></div>
          <div><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Departure</span>
            <DatePicker value={(f.departure || '').slice(0,10)} onChange={(_v) => setF({...f, departure: _v})} data-testid="guest-stay-departure"/></div>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Adults</span>
            <NumberInput placeholder="e.g. 2" value={f.adults} onChange={e=>setF({...f, adults: e.target.value})} className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Children</span>
            <NumberInput placeholder="e.g. 0" value={f.children} onChange={e=>setF({...f, children: e.target.value})} className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Revenue (₹)</span>
            <NumberInput placeholder="e.g. 12000" value={f.revenue} onChange={e=>setF({...f, revenue: e.target.value})} data-testid="guest-stay-revenue" className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Status</span>
            <select value={f.status} onChange={e=>setF({...f, status: e.target.value})} data-testid="guest-stay-status" className={cls + " w-full"}>
              <option value="reserved">Reserved</option><option value="checked_in">Checked In</option>
              <option value="checked_out">Checked Out</option><option value="cancelled">Cancelled</option>
              <option value="no_show">No-show</option>
            </select></label>
          <label className="col-span-2 block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Rate plan</span>
            <input placeholder="e.g. BB / EP" value={f.rate_plan} onChange={e=>setF({...f, rate_plan: e.target.value})} className={cls + " w-full"}/></label>
        </div>
        <div className="px-5 py-3 bg-slate-50 flex justify-end gap-2 border-t border-slate-100">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="guest-stay-submit"
                  className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4"/>{saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Preferences ───
function TabPreferences({ g, onChanged }) {
  const [prefs, setPrefs] = useState(g.preferences || {});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setPrefs({...prefs, [k]: v});
  const save = async () => {
    setSaving(true);
    try { await guestAPI.update(g.id, { preferences: prefs, reason: "preferences update" });
         toast.success("Preferences saved"); onChanged(); }
    catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };
  const chips = (k, options) => (
    <div className="flex flex-wrap gap-1.5">
      {options.map(o => (
        <button key={o} onClick={()=>set(k, prefs[k] === o ? null : o)}
                data-testid={`guest-pref-${k}-${o}`}
                className={`h-8 px-3 rounded-lg text-[12px] font-medium border ${
                  prefs[k] === o ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
          {o}
        </button>
      ))}
    </div>
  );
  return (
    <div className="space-y-4" data-testid="guest-tab-preferences">
      <PrefGroup label="Bed">      {chips("bed", ["king","twin","queen"])}</PrefGroup>
      <PrefGroup label="Floor">    {chips("floor", ["high","low","near_elevator","away_from_elevator"])}</PrefGroup>
      <PrefGroup label="Smoking">  {chips("smoking", ["smoking","non_smoking"])}</PrefGroup>
      <PrefGroup label="Food">     {chips("food", ["vegetarian","vegan","jain","halal","kosher","no_beef","no_pork"])}</PrefGroup>
      <PrefGroup label="Pillow">   {chips("pillow", ["soft","medium","firm"])}</PrefGroup>
      <PrefGroup label="Extras">
        <input placeholder="Favourite drink" aria-label="Favourite drink" value={prefs.favourite_drink || ""} onChange={e=>set("favourite_drink", e.target.value)} className={cls}/>
        <input placeholder="Newspaper" aria-label="Newspaper" value={prefs.newspaper || ""} onChange={e=>set("newspaper", e.target.value)} className={cls + " mt-2"}/>
        <textarea placeholder="Allergies / dietary restrictions" aria-label="Allergies / dietary restrictions" rows={2} value={prefs.allergies || ""} onChange={e=>set("allergies", e.target.value)} className={cls + " mt-2 h-auto py-2"}/>
        <textarea placeholder="Special notes" aria-label="Special notes" rows={2} value={prefs.notes || ""} onChange={e=>set("notes", e.target.value)} className={cls + " mt-2 h-auto py-2"}/>
      </PrefGroup>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="guest-pref-save"
                className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
          <Save className="w-4 h-4"/>{saving ? "Saving…" : "Save Preferences"}
        </button>
      </div>
    </div>
  );
}
function PrefGroup({ label, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-2">{label}</div>
      {children}
    </div>
  );
}

// ─── Timeline ───
function TabTimeline({ gid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { guestAPI.timeline(gid).then(setRows); }, [gid]);
  return (
    <div data-testid="guest-tab-timeline">
      {rows.length === 0
        ? <div className="p-8 text-center text-[13px] text-slate-400">No timeline events yet.</div>
        : <div className="border-l-2 border-slate-200 ml-3 pl-5 space-y-3">
            {rows.map(e => (
              <div key={e.id} className="relative" data-testid={`guest-tl-${e.id}`}>
                <div className={`absolute -left-[26px] top-1 w-3 h-3 rounded-full border-2 border-white ${
                  e.priority === "critical" ? "bg-rose-500" : e.priority === "high" ? "bg-amber-500" : "bg-hocp-navy"
                }`}/>
                <div className="text-[10.5px] uppercase text-slate-400 tracking-wider">{fmtDT(e.at)} · {e.kind}</div>
                <div className="text-[13px] font-medium text-hocp-navy">{e.title}</div>
                {e.description && <div className="text-[12px] text-slate-500">{e.description}</div>}
              </div>
            ))}
          </div>}
    </div>
  );
}

// ─── Documents ───
function TabDocuments({ gid }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ kind:"passport", label:"", url:"" });
  const load = () => guestAPI.documents(gid).then(setRows);
  useEffect(() => { load(); }, [gid]);   // eslint-disable-line
  const add = async () => {
    if (!f.label && !f.url) { toast.error("Add label or URL"); return; }
    try { await guestAPI.addDoc(gid, f); setF({...f, label:"", url:""}); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (did) => {
    if (!window.confirm("Delete this document?")) return;
    try { await guestAPI.delDoc(gid, did); load(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="guest-tab-documents">
      <div className="bg-white border border-slate-200 rounded-xl p-3 grid grid-cols-4 gap-2">
        <select value={f.kind} onChange={e=>setF({...f, kind: e.target.value})} data-testid="guest-doc-kind" className={cls}>
          {["passport","visa","id","registration","invoice","other"].map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <input placeholder="Label" aria-label="Document label" value={f.label} onChange={e=>setF({...f, label: e.target.value})} data-testid="guest-doc-label" className={cls}/>
        <input placeholder="URL" aria-label="Document URL"   value={f.url}   onChange={e=>setF({...f, url: e.target.value})}   data-testid="guest-doc-url" className={cls}/>
        <button onClick={add} data-testid="guest-doc-add"
                className="h-10 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center justify-center gap-1.5">
          <Plus className="w-4 h-4"/> Attach
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {rows.length === 0
          ? <div className="p-8 text-center text-[13px] text-slate-400">No documents attached.</div>
          : rows.map(d => (
              <div key={d.id} data-testid={`guest-doc-${d.id}`} className="px-4 py-2.5 flex items-center gap-3">
                <FileText className="w-4 h-4 text-slate-500"/>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-hocp-navy">{d.label || d.kind}</div>
                  <div className="text-[11px] text-slate-500 mono">{d.kind} · {fmtDT(d.created_at)}</div>
                </div>
                {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-[12px] text-hocp-navy hover:underline">Open</a>}
                <button onClick={()=>del(d.id)} data-testid={`guest-doc-del-${d.id}`}
                        className="h-7 w-7 rounded-lg text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center">
                  <Trash2 className="w-3.5 h-3.5"/>
                </button>
              </div>
            ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="guest-stay-documents-section">
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mono mb-3">Stay Documents</div>
        <GuestStayDocuments guestId={gid} />
      </div>
    </div>
  );
}

// ─── Communication ───
function TabCommunication({ gid }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ channel:"note", direction:"internal", subject:"", body:"" });
  const load = () => guestAPI.communications(gid).then(setRows);
  useEffect(() => { load(); }, [gid]);   // eslint-disable-line
  const send = async () => {
    if (!f.body.trim()) return;
    try { await guestAPI.addComm(gid, f); setF({...f, subject:"", body:""}); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="guest-tab-communication">
      <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2">
        <div className="grid grid-cols-4 gap-2">
          <select value={f.channel} onChange={e=>setF({...f, channel: e.target.value})} data-testid="guest-comm-channel" className={cls}>
            {["email","phone","whatsapp","note","in_person"].map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select value={f.direction} onChange={e=>setF({...f, direction: e.target.value})} className={cls}>
            <option value="inbound">Inbound</option><option value="outbound">Outbound</option><option value="internal">Internal note</option>
          </select>
          <input placeholder="Subject" aria-label="Subject" value={f.subject} onChange={e=>setF({...f, subject: e.target.value})} className={cls + " col-span-2"}/>
        </div>
        <textarea value={f.body} onChange={e=>setF({...f, body: e.target.value})} rows={3}
                  placeholder="Body / conversation summary…"
                  data-testid="guest-comm-body" className={cls + " h-auto py-2"}/>
        <div className="flex justify-end">
          <button onClick={send} data-testid="guest-comm-send"
                  className="h-9 px-4 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium inline-flex items-center gap-2">
            <Send className="w-3.5 h-3.5"/> Log
          </button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {rows.length === 0
          ? <div className="p-8 text-center text-[13px] text-slate-400">No communications logged.</div>
          : rows.map(c => (
              <div key={c.id} className="p-3" data-testid={`guest-comm-${c.id}`}>
                <div className="text-[11px] text-slate-500 mono flex items-center gap-2">
                  <span className="uppercase">{c.channel}</span> · {c.direction} · {fmtDT(c.at)} · <span>by {c.actor_email}</span>
                </div>
                {c.subject && <div className="text-[13px] font-medium text-hocp-navy mt-0.5">{c.subject}</div>}
                <div className="text-[12.5px] text-slate-700 mt-0.5 whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
      </div>
    </div>
  );
}

// ─── Loyalty ───
function TabLoyalty({ gid }) {
  const [l, setL] = useState(null);
  const [pts, setPts] = useState("");
  const [reason, setReason] = useState("");
  const load = () => guestAPI.loyalty(gid).then(setL);
  useEffect(() => { load(); }, [gid]);   // eslint-disable-line
  const adjust = async () => {
    if (!pts || !reason.trim()) { toast.error("Enter points and reason"); return; }
    try { await guestAPI.adjustLoyalty(gid, { points: Number(pts), reason }); setPts(""); setReason(""); load(); }
    catch { toast.error("Failed"); }
  };
  if (!l) return <div className="p-8 text-center text-[13px] text-slate-400">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="guest-tab-loyalty">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Points" value={l.points || 0}/>
        <StatCard label="Tier" value={(l.tier || "standard").toUpperCase()}/>
        <StatCard label="Lifetime Spend" value={fmtINR(l.lifetime_spend || 0)}/>
        <StatCard label="Visits" value={l.visits || 0}/>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2">
        <NumberInput placeholder="Points (+/-)" aria-label="Loyalty points (+/-)" value={pts} onChange={e=>setPts(e.target.value)} data-testid="guest-loyalty-points" className={cls + " w-40"}/>
        <input placeholder="Reason" aria-label="Reason" value={reason} onChange={e=>setReason(e.target.value)} data-testid="guest-loyalty-reason" className={cls + " flex-1"}/>
        <button onClick={adjust} data-testid="guest-loyalty-adjust"
                className="h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium">
          Adjust
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {(l.rewards || []).length === 0
          ? <div className="p-8 text-center text-[13px] text-slate-400">No reward history.</div>
          : l.rewards.slice().reverse().map((r, i) => (
              <div key={i} className="p-3 flex items-center gap-3 text-[13px]">
                <div className={`font-mono font-semibold tabular-nums w-16 ${r.points >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  {r.points >= 0 ? "+" : ""}{r.points}
                </div>
                <div className="flex-1">{r.reason}</div>
                <div className="text-[11px] mono text-slate-400">{fmtDT(r.at)}</div>
              </div>
            ))}
      </div>
    </div>
  );
}

// ─── Family ───
function TabFamily({ gid }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ other_guest_id:"", relation:"spouse", note:"" });
  const load = () => guestAPI.relations(gid).then(setRows);
  useEffect(() => { load(); }, [gid]);   // eslint-disable-line
  const add = async () => {
    if (!f.other_guest_id) { toast.error("Enter the related guest ID"); return; }
    try { await guestAPI.addRelation(gid, f); setF({ other_guest_id:"", relation:"spouse", note:"" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (rid) => {
    try { await guestAPI.delRelation(gid, rid); load(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="guest-tab-family">
      <div className="bg-white border border-slate-200 rounded-xl p-3 grid grid-cols-4 gap-2">
        <input placeholder="Other guest ID" aria-label="Other guest ID" value={f.other_guest_id} onChange={e=>setF({...f, other_guest_id: e.target.value})} data-testid="guest-rel-id" className={cls}/>
        <select value={f.relation} onChange={e=>setF({...f, relation: e.target.value})} data-testid="guest-rel-kind" className={cls}>
          {["spouse","child","parent","friend","companion","colleague","group_member","other"].map(r=><option key={r} value={r}>{r}</option>)}
        </select>
        <input placeholder="Note (optional)" aria-label="Note" value={f.note} onChange={e=>setF({...f, note: e.target.value})} className={cls}/>
        <button onClick={add} data-testid="guest-rel-add"
                className="h-10 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center justify-center gap-1.5">
          <Plus className="w-4 h-4"/> Add
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {rows.length === 0
          ? <div className="p-8 text-center text-[13px] text-slate-400">No family or relationships linked.</div>
          : rows.map(r => (
              <div key={r.id} className="p-3 flex items-center gap-3" data-testid={`guest-rel-${r.id}`}>
                <div className="w-8 h-8 rounded-full bg-hocp-navy text-white text-[11px] font-semibold flex items-center justify-center">
                  {(r.other?.first_name || "?").charAt(0)}
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-medium text-hocp-navy">{r.other?.first_name} {r.other?.last_name || ""}</div>
                  <div className="text-[11px] text-slate-500 mono">{r.relation}{r.note ? ` · ${r.note}` : ""}</div>
                </div>
                <button onClick={()=>del(r.id)} data-testid={`guest-rel-del-${r.id}`}
                        className="h-7 w-7 rounded-lg text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center">
                  <Trash2 className="w-3.5 h-3.5"/>
                </button>
              </div>
            ))}
      </div>
    </div>
  );
}

// ─── Complaints ───
function TabComplaints({ gid }) {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = () => guestAPI.complaints(gid).then(setRows);
  useEffect(() => { load(); }, [gid]);   // eslint-disable-line
  const resolve = async (c) => {
    const resolution = window.prompt("Resolution?");
    if (!resolution) return;
    try { await guestAPI.updateComplaint(c.id, { status: "resolved", resolution }); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="guest-tab-complaints">
      <div className="flex justify-end">
        <button onClick={()=>setCreating(true)} data-testid="guest-comp-new"
                className="h-9 px-3 rounded-lg bg-hocp-gold text-hocp-eerie text-[12.5px] font-medium inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5"/> Log Complaint
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {rows.length === 0
          ? <div className="p-8 text-center text-[13px] text-slate-400">No complaints on file.</div>
          : rows.map(c => (
              <div key={c.id} className="p-3" data-testid={`guest-comp-${c.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${
                    c.status === "open" ? "bg-rose-100 text-rose-700" :
                    c.status === "in_progress" ? "bg-amber-100 text-amber-800" :
                    c.status === "resolved" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{(c.status||"").replace("_"," ")}</span>
                  <div className="text-[13px] font-medium text-hocp-navy">{c.title}</div>
                  <div className="ml-auto text-[11px] mono text-slate-400">{fmtDT(c.created_at)}</div>
                </div>
                {c.description && <div className="text-[12.5px] text-slate-600 mt-1">{c.description}</div>}
                {c.resolution && <div className="text-[12.5px] text-emerald-700 mt-1"><Check size={14} strokeWidth={2} className="inline mr-1 -mt-0.5" />{c.resolution}</div>}
                <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500">
                  {c.department && <span>Dept: {c.department}</span>}
                  {c.compensation != null && <span>Comp: {fmtINR(c.compensation)}</span>}
                  {c.refund != null && <span>Refund: {fmtINR(c.refund)}</span>}
                  {c.status !== "resolved" && c.status !== "closed" && (
                    <button onClick={()=>resolve(c)} data-testid={`guest-comp-resolve-${c.id}`}
                            className="ml-auto h-7 px-2.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-[11px] font-medium">
                      Resolve
                    </button>
                  )}
                </div>
              </div>
            ))}
      </div>
      {creating && <NewComplaintDialog gid={gid} onClose={()=>setCreating(false)} onDone={()=>{ setCreating(false); load(); }}/>}
    </div>
  );
}
function NewComplaintDialog({ gid, onClose, onDone }) {
  const [f, setF] = useState({ title:"", description:"", priority:"normal", department:"" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try { await guestAPI.addComplaint(gid, f); toast.success("Complaint logged"); onDone(); }
    catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">Log Complaint</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5 space-y-2">
          <input value={f.title} onChange={e=>setF({...f, title: e.target.value})} placeholder="Title" aria-label="Title" data-testid="guest-comp-title" className={cls}/>
          <textarea value={f.description} onChange={e=>setF({...f, description: e.target.value})} rows={3} placeholder="Description" aria-label="Description" data-testid="guest-comp-desc" className={cls + " h-auto py-2"}/>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Department" aria-label="Department" value={f.department} onChange={e=>setF({...f, department: e.target.value})} className={cls}/>
            <select value={f.priority} onChange={e=>setF({...f, priority: e.target.value})} className={cls}>
              {["low","normal","high","critical"].map(p=><option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div className="px-5 py-3 bg-slate-50 flex justify-end gap-2 border-t border-slate-100">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="guest-comp-submit"
                  className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4"/>{saving ? "Saving…" : "Log Complaint"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Audit ───
function TabAudit({ gid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { guestAPI.audit(gid).then(setRows); }, [gid]);
  return (
    <div data-testid="guest-tab-audit">
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {rows.length === 0
          ? <div className="p-8 text-center text-[13px] text-slate-400">No audit trail yet.</div>
          : rows.map(e => (
              <div key={e.id} className="p-3" data-testid={`guest-audit-${e.id}`}>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-700">{e.action}</span>
                  <span className="text-[11.5px] mono text-slate-500">{e.actor_email}</span>
                  <span className="ml-auto text-[11px] mono text-slate-400">{fmtDT(e.at)}</span>
                </div>
                {e.reason && <div className="text-[12px] italic text-slate-500 mt-1">&quot;{e.reason}&quot;</div>}
                {e.after && Object.keys(e.after || {}).length > 0 && (
                  <div className="text-[11.5px] text-slate-600 mt-1 mono">
                    {Object.entries(e.after).slice(0,5).map(([k,v]) => (
                      <div key={k}><span className="text-slate-400">{k}:</span> {String(v).slice(0,60)}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
      </div>
    </div>
  );
}

// ─── Wallet (reads Finance OS folios via /guest/v1/guests/{id}/wallet) ───
function TabWallet({ g }) {
  const [w, setW]         = useState(null);
  const [stays, setStays] = useState([]);
  useEffect(() => {
    guestAPI.wallet(g.id).then(setW).catch(()=>setW({ summary:{}, folios:[], invoices:[]}));
    guestAPI.stays(g.id).then(setStays);
  }, [g.id]);
  if (!w) return <div className="p-8 text-center text-[13px] text-slate-400">Loading wallet…</div>;
  const sum = w.summary || {};
  const s = g.stats || {};
  const stayRev   = s.revenue || 0;
  const avgSpend  = s.avg_stay_rev || 0;
  return (
    <div className="space-y-4" data-testid="guest-tab-wallet">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Lifetime Revenue" value={fmtINR(stayRev)}/>
        <StatCard label="Total Charges"    value={fmtINR(sum.total_charges || 0)}/>
        <StatCard label="Total Payments"   value={fmtINR(sum.total_payments || 0)}/>
        <StatCard label="Open Balance"     value={fmtINR(sum.open_balance || 0)}/>
      </div>

      {/* Finance folios */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="guest-wallet-folios">
        <div className="px-4 py-3 border-b border-slate-100 font-display text-[13px] font-semibold text-hocp-navy">
          Finance Folios ({(w.folios || []).length})
        </div>
        {(w.folios || []).length === 0
          ? <div className="p-8 text-center text-[13px] text-slate-400">No folios linked to this guest yet.</div>
          : <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr><th className="text-left px-4 py-2">Folio</th><th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Charges</th><th className="text-right px-4 py-2">Payments</th>
                    <th className="text-right px-4 py-2">Balance</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {w.folios.map(f => (
                  <tr key={f.id} data-testid={`guest-wallet-folio-${f.id}`}>
                    <td className="px-4 py-2 mono">{f.folio_no}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${
                        f.status === "open" ? "bg-emerald-100 text-emerald-700" :
                        f.status === "closed" ? "bg-slate-100 text-slate-600" : "bg-rose-100 text-rose-700"}`}>{f.status}</span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtINR(f.total_charges)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtINR(f.total_payments)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtINR(f.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>

      {/* Stay payment history — GUEST-1 mirrored view */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 font-display text-[13px] font-semibold text-hocp-navy">
          Stay Payment History
        </div>
        {stays.length === 0
          ? <div className="p-8 text-center text-[13px] text-slate-400">No stays recorded yet.</div>
          : <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr><th className="text-left px-4 py-2">Arrival</th><th className="text-left px-4 py-2">Room</th>
                    <th className="text-left px-4 py-2">Payment</th><th className="text-right px-4 py-2">Revenue</th>
                    <th className="text-center px-4 py-2">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stays.map(x => (
                  <tr key={x.id} data-testid={`guest-wallet-stay-${x.id}`}>
                    <td className="px-4 py-2 mono">{x.arrival}</td>
                    <td className="px-4 py-2">{x.room} <span className="text-[11px] text-slate-500">{x.room_type}</span></td>
                    <td className="px-4 py-2 text-[12px] text-slate-500">{x.payment_method || "—"}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtINR(x.revenue || 0)}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${
                        x.status === "checked_in" ? "bg-emerald-100 text-emerald-700" :
                        x.status === "checked_out" ? "bg-slate-100 text-slate-600" : "bg-sky-100 text-sky-700"}`}>{(x.status||"").replace("_"," ")}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>

      <div className="bg-sky-50/60 border border-sky-200 rounded-xl p-3 text-[12px] text-sky-900">
        Avg Spend / Stay: <span className="font-semibold">{fmtINR(avgSpend)}</span> · Folios shown are Finance OS authoritative source of truth (charges, payments &amp; balances).
      </div>
    </div>
  );
}

// ─── Dining / Housekeeping / Maintenance — direct engine reads ───
function TabDining({ gid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { guestAPI.dining(gid).then(setRows).catch(()=>setRows([])); }, [gid]);
  if (rows === null) return <div className="p-8 text-center text-[13px] text-slate-400">Loading…</div>;
  return (
    <div data-testid="guest-tab-dining">
      {rows.length === 0
        ? <div className="p-10 text-center text-[13px] text-slate-400">No dining activity linked yet.</div>
        : <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
            {rows.map(o => (
              <div key={o.id} className="p-3" data-testid={`guest-dining-order-${o.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-wider text-slate-400">{fmtDT(o.created_at)} · {o.status}</div>
                    <div className="text-[13px] font-medium text-hocp-navy">{o.code} · {(o.service_location_kind||"").replace("_"," ")}</div>
                    <div className="text-[12px] text-slate-500">{(o.lines || []).length} items{o.room_number ? ` · Room ${o.room_number}` : ""}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[13px] font-semibold text-hocp-navy tabular-nums">{fmtINR(Number(o.total || 0))}</div>
                    <div className="text-[10.5px] text-slate-400">{o.business_date}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>}
    </div>
  );
}

function TabHousekeeping({ gid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { guestAPI.housekeeping(gid).then(setRows).catch(()=>setRows([])); }, [gid]);
  if (rows === null) return <div className="p-8 text-center text-[13px] text-slate-400">Loading…</div>;
  return (
    <div data-testid="guest-tab-housekeeping">
      {rows.length === 0
        ? <div className="p-10 text-center text-[13px] text-slate-400">No housekeeping requests linked yet.</div>
        : <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
            {rows.map(t => (
              <div key={t.id} className="p-3" data-testid={`guest-hk-task-${t.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-wider text-slate-400">
                      {fmtDT(t.created_at)} · {t.state} · {t.priority_code || "NORMAL"}
                    </div>
                    <div className="text-[13px] font-medium text-hocp-navy">
                      {(t.task_type || "").replace(/_/g," ").replace(/\b\w/g, c=>c.toUpperCase())} — Room {t.room_number || "?"}
                    </div>
                    {t.notes && <div className="text-[12px] text-slate-500">{t.notes}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10.5px] text-slate-400">SLA {t.estimated_minutes || "—"}m</div>
                  </div>
                </div>
              </div>
            ))}
          </div>}
    </div>
  );
}

function TabMaintenance({ gid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { guestAPI.maintenance(gid).then(setRows).catch(()=>setRows([])); }, [gid]);
  if (rows === null) return <div className="p-8 text-center text-[13px] text-slate-400">Loading…</div>;
  return (
    <div data-testid="guest-tab-maintenance">
      {rows.length === 0
        ? <div className="p-10 text-center text-[13px] text-slate-400">No maintenance tickets linked yet.</div>
        : <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
            {rows.map(w => (
              <div key={w.id} className="p-3" data-testid={`guest-maint-wo-${w.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-wider text-slate-400">
                      {fmtDT(w.created_at)} · {w.status} · {w.priority}
                    </div>
                    <div className="text-[13px] font-medium text-hocp-navy">{w.title} <span className="mono text-slate-400">· {w.code}</span></div>
                    {w.description && <div className="text-[12px] text-slate-500">{w.description}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10.5px] text-slate-400">{w.category || ""}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>}
    </div>
  );
}

// ─── Analytics ───
function TabAnalytics({ gid }) {
  const [a, setA] = useState(null);
  useEffect(() => { guestAPI.analytics(gid).then(setA); }, [gid]);
  if (!a) return <div className="p-8 text-center text-[13px] text-slate-400">Loading analytics…</div>;
  const maxDept = Math.max(1, ...a.dept_spend.map(x => x.amount));
  const maxMo   = Math.max(1, ...a.monthly.map(x => x.revenue));
  return (
    <div className="space-y-4" data-testid="guest-tab-analytics">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Stays" value={a.stays || 0}/>
        <StatCard label="Lifetime Revenue" value={fmtINR(a.lifetime_revenue)}/>
        <StatCard label="Avg Spend" value={fmtINR(a.avg_spend)}/>
        <StatCard label="Favourite Room" value={a.favourite_room || "—"}/>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-3">Department Spend</div>
        {a.dept_spend.length === 0
          ? <div className="text-[12.5px] text-slate-400">No department spend yet.</div>
          : a.dept_spend.map(d => (
              <div key={d.department} className="mb-2" data-testid={`guest-analytics-dept-${d.department}`}>
                <div className="flex justify-between text-[12.5px] mb-1">
                  <span className="text-hocp-navy font-medium">{d.department}</span>
                  <span className="mono tabular-nums">{fmtINR(d.amount)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-hocp-gold" style={{ width: `${(d.amount/maxDept)*100}%` }}/>
                </div>
              </div>
            ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-3">Monthly Revenue</div>
        {a.monthly.length === 0
          ? <div className="text-[12.5px] text-slate-400">No monthly revenue data.</div>
          : (
            <div className="flex items-end gap-1.5 h-32">
              {a.monthly.map(m => (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1" title={`${m.month}: ${fmtINR(m.revenue)}`}>
                  <div className="w-full bg-hocp-navy rounded-t" style={{ height: `${(m.revenue/maxMo)*100}%`, minHeight: 2 }}/>
                  <div className="text-[9px] text-slate-400 tabular-nums">{m.month.slice(5)}</div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

// ─── Filtered timeline (dining/hk/maintenance placeholders) ───
function TabTimelineFiltered({ gid, testid, kinds, empty }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    guestAPI.timeline(gid).then(all => {
      setRows(kinds ? all.filter(e => kinds.some(k => e.kind?.startsWith(k))) : all);
    });
  }, [gid, kinds ? kinds.join(",") : ""]);   // eslint-disable-line
  if (rows === null) return <div className="p-8 text-center text-[13px] text-slate-400">Loading…</div>;
  return (
    <div data-testid={testid}>
      {rows.length === 0
        ? <div className="p-10 text-center text-[13px] text-slate-400">{empty || "Nothing here yet."}</div>
        : <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
            {rows.map(e => (
              <div key={e.id} className="p-3">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-400">{fmtDT(e.at)} · {e.kind}</div>
                <div className="text-[13px] font-medium text-hocp-navy">{e.title}</div>
                {e.description && <div className="text-[12px] text-slate-500">{e.description}</div>}
              </div>
            ))}
          </div>}
    </div>
  );
}
